import { PrismicRichText } from '@prismicio/react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import styles from './HomepageCollectionsGrid.module.css'

const HomepageCollectionsGrid = ({ slice }) => {
  const { container, mobileGridLayout, tilesPerPage } = slice.primary
  const router = useRouter()
  const isHomepage = router.pathname === '/'
  const allItems = slice.items
  const isGridLayout = mobileGridLayout === 'grid'
  const desktopCols = tilesPerPage
    ? Math.min(tilesPerPage, allItems.length)
    : allItems.length
  const sliderRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesVisible, setSlidesVisible] = useState(() => {
    if (typeof window === 'undefined') return allItems.length
    const width = window.innerWidth
    if (width < 768) return 2.5
    if (width < 1024) return 4.5
    return allItems.length
  })
  const [isAtEnd, setIsAtEnd] = useState(false)

  useEffect(() => {
    const updateSlidesVisible = () => {
      const width = window.innerWidth

      if (width < 768) {
        setSlidesVisible(2.5)
        return
      }

      if (width < 1024) {
        setSlidesVisible(4.5)
        return
      }

      setSlidesVisible(allItems.length)
    }

    updateSlidesVisible()
    window.addEventListener('resize', updateSlidesVisible)

    return () => {
      window.removeEventListener('resize', updateSlidesVisible)
    }
  }, [allItems.length])

  useEffect(() => {
    setCurrentSlide(0)
    setIsAtEnd(false)
  }, [slidesVisible])

  const scrollBy =
    slidesVisible <= 2.5 ? 2 : slidesVisible <= 4.5 ? 4 : allItems.length
  const dotCount = Math.ceil(allItems.length / scrollBy)
  const activeDot = isAtEnd
    ? dotCount - 1
    : Math.max(0, Math.min(Math.floor(currentSlide / scrollBy), dotCount - 1))

  const updateEndState = (slideIdx) => {
    const maxStartIndex = Math.max(0, allItems.length - slidesVisible)
    setIsAtEnd(slideIdx >= maxStartIndex - 0.01)
  }

  const settings = {
    dots: false,
    infinite: false,
    initialSlide: 0,
    speed: 500,
    slidesToShow: allItems.length,
    slidesToScroll: allItems.length,
    arrows: false,
    beforeChange: (_, nextSlide) => {
      setCurrentSlide(nextSlide)
      updateEndState(nextSlide)
    },
    afterChange: (slide) => {
      setCurrentSlide(slide)
      updateEndState(slide)
    },
    responsive: [
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 4.5,
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2.5,
          slidesToScroll: 2,
        },
      },
    ],
  }

  const sliderKey = `${slidesVisible}-${allItems.length}`

  const renderItem = (item, i) => (
    <Link href={item.link ? item.link : '#'} key={i} legacyBehavior>
      <div className={styles.grid__item}>
        <div className={styles.grid__imageWrapper}>
          <Image
            src={item.image.url}
            alt={item.image.alt || slice.primary.heading?.[0]?.text || 'image'}
            width={item.image.dimensions?.width}
            height={item.image.dimensions?.height}
            layout="responsive"
            unoptimized
          />
        </div>
        {item.title?.[0]?.text && (
          <div className={styles.grid__itemTitle}>
            <PrismicRichText field={item.title} />
          </div>
        )}
      </div>
    </Link>
  )

  return (
    <div className={`${(container && styles.wrapper) || styles.wrapper}`}>
      <div className={styles.homeCollectionsRow}>
        {slice.primary.heading?.[0]?.text && (
          <div className={styles.grid__heading}>
            <PrismicRichText field={slice.primary.heading} />
          </div>
        )}
        {isGridLayout ? (
          <div
            className={`${styles.gridModeBody}${
              isHomepage ? ` ${styles.gridModeBodyHideLastOdd}` : ''
            }`}
            style={{ '--grid-cols': desktopCols }}
          >
            {allItems.map((item, i) => renderItem(item, i))}
          </div>
        ) : (
          <div
            className={`${styles.sliderWrapper} grid-slick ${
              isAtEnd ? 'grid-slick-at-end' : ''
            }`}
          >
            <Slider ref={sliderRef} key={sliderKey} {...settings}>
              {allItems.map((item, i) => renderItem(item, i))}
            </Slider>
            {dotCount > 1 && (
              <ul className="slick-dots">
                {Array.from({ length: dotCount }, (_, i) => (
                  <li key={i} className={i === activeDot ? 'slick-active' : ''}>
                    <button
                      type="button"
                      onClick={() => sliderRef.current?.slickGoTo(i * scrollBy)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default HomepageCollectionsGrid
